/**
 * Pinned Sessions Store
 *
 * Tracks which chat sessions the user has pinned to the top of the sidebar.
 * Stored locally in localStorage for instant feedback; can be migrated to
 * server-side sync later if cross-device behavior is needed.
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

export type PinnedSessionsState = {
  /** Map of sessionId → timestamp when it was pinned (for stable ordering). */
  pinned: Record<string, number>;
};

export type PinnedSessionsActions = {
  pin: (sessionId: string) => void;
  unpin: (sessionId: string) => void;
  togglePin: (sessionId: string) => void;
  isPinned: (sessionId: string) => boolean;
  clearAll: () => void;
};

export type PinnedSessionsStore = PinnedSessionsState & PinnedSessionsActions;

const MAX_PINNED = 20; // Safety cap — pinning everything defeats the purpose

export const usePinnedSessionsStore = create<PinnedSessionsStore>()(
  devtools(
    persist(
      (set, get) => ({
        pinned: {},

        pin: (sessionId: string) => {
          const { pinned } = get();
          if (pinned[sessionId]) return; // Already pinned
          if (Object.keys(pinned).length >= MAX_PINNED) return; // Cap reached
          set(state => ({
            pinned: { ...state.pinned, [sessionId]: Date.now() },
          }));
        },

        unpin: (sessionId: string) => {
          set(state => {
            if (!state.pinned[sessionId]) return state;
            const next = { ...state.pinned };
            delete next[sessionId];
            return { pinned: next };
          });
        },

        togglePin: (sessionId: string) => {
          const { pinned, pin, unpin } = get();
          if (pinned[sessionId]) {
            unpin(sessionId);
          } else {
            pin(sessionId);
          }
        },

        isPinned: (sessionId: string) => {
          return Boolean(get().pinned[sessionId]);
        },

        clearAll: () => set({ pinned: {} }),
      }),
      {
        name: 'pinned-sessions-storage',
        version: 1,
      }
    ),
    { name: 'pinned-sessions-store' }
  )
);

/** Selector: stable set of pinned session IDs. */
export const usePinnedSessionIds = (): Set<string> =>
  usePinnedSessionsStore(state => new Set(Object.keys(state.pinned)));

/** Selector: whether a specific session is pinned — cheap subscription. */
export const useIsSessionPinned = (sessionId: string | null | undefined): boolean =>
  usePinnedSessionsStore(state => (sessionId ? Boolean(state.pinned[sessionId]) : false));
